import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSignals } from '../api/client';
import SignalBadge from '../components/SignalBadge';
import DataTable from '../components/DataTable';
import type { Signal } from '../types';

const SIGNAL_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'SHARP_VS_SQUARE', label: 'Sharp vs Square' },
  { value: 'STEAM_MOVE', label: 'Steam Move' },
  { value: 'CONSENSUS_DIVERGENCE', label: 'Consensus Divergence' },
  { value: 'OPENING_VS_CURRENT', label: 'Opening vs Current' },
  { value: 'ARBITRAGE', label: 'Arbitrage' },
  { value: 'CLV_POSITIVE', label: 'CLV Positive' },
  { value: 'VOLUME_SPIKE', label: 'Volume Spike' },
];

// T522: Market type filter options
const MARKET_TYPES = [
  { value: '', label: 'All Markets' },
  { value: 'h2h', label: 'Moneyline (H2H)' },
  { value: 'spreads', label: 'Spreads' },
  { value: 'totals', label: 'Totals (O/U)' },
];

// T522: Market type display labels
const MARKET_TYPE_LABELS: Record<string, string> = {
  h2h: 'ML',
  spreads: 'SPR',
  totals: 'O/U',
};

export default function Signals() {
  const [typeFilter, setTypeFilter] = useState('');
  const [marketFilter, setMarketFilter] = useState('');
  const [minStrength, setMinStrength] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['signals', { type: typeFilter, market: marketFilter, minStrength }],
    queryFn: () => fetchSignals({ type: typeFilter || undefined, market: marketFilter || undefined, minStrength }),
  });

  const columns = [
    {
      key: 'signalType',
      header: 'Type',
      render: (signal: Signal) => <SignalBadge type={signal.signalType} />,
    },
    {
      key: 'event',
      header: 'Event',
      render: (signal: Signal) => (
        <Link
          to={`/events/${signal.eventId}`}
          className="block hover:text-accent transition-colors"
        >
          <div className="font-medium">
            {signal.event.homeTeam} vs {signal.event.awayTeam}
          </div>
          <div className="text-xs text-gray-400">{signal.event.sport}</div>
        </Link>
      ),
    },
    {
      key: 'strength',
      header: 'Strength',
      render: (signal: Signal) => (
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${signal.strength * 100}%` }}
            />
          </div>
          <span className="text-sm">{(signal.strength * 100).toFixed(0)}%</span>
        </div>
      ),
    },
    {
      key: 'marketType',
      header: 'Market',
      render: (signal: Signal) => (
        <span className={`text-xs px-2 py-0.5 rounded ${
          signal.marketType === 'h2h' ? 'bg-blue-500/20 text-blue-400' :
          signal.marketType === 'spreads' ? 'bg-purple-500/20 text-purple-400' :
          signal.marketType === 'totals' ? 'bg-green-500/20 text-green-400' :
          'bg-gray-500/20 text-gray-400'
        }`}>
          {MARKET_TYPE_LABELS[signal.marketType] || signal.marketType || 'N/A'}
        </span>
      ),
    },
    {
      key: 'direction',
      header: 'Direction',
      render: (signal: Signal) => (
        <span className="flex items-center gap-1">
          {signal.direction === 'home' || signal.direction === 'over' ? '↑' : '↓'}
          <span className="capitalize">{signal.direction}</span>
        </span>
      ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      render: (signal: Signal) => (
        <span className={signal.confidence > 0.7 ? 'text-success' : signal.confidence > 0.5 ? 'text-warning' : 'text-gray-400'}>
          {(signal.confidence * 100).toFixed(0)}%
        </span>
      ),
    },
    {
      key: 'detectedAt',
      header: 'Age',
      render: (signal: Signal) => (
        <span className="text-gray-400">{formatTimeAgo(signal.detectedAt)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Live Signals</h1>
        <span className="text-sm text-gray-400">
          {data?.signals.length || 0} active signals
        </span>
      </div>

      {/* Signal type counts */}
      {data?.countsByType && (
        <div className="flex gap-4">
          {Object.entries(data.countsByType).map(([type, count]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                typeFilter === type
                  ? 'border-accent bg-accent/20'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <SignalBadge type={type} showLabel={false} size="sm" />
              <span className="text-sm">{count as number}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          {SIGNAL_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={marketFilter}
          onChange={(e) => setMarketFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          {MARKET_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Min Strength:</label>
          <input
            type="range"
            min="0"
            max="100"
            value={minStrength * 100}
            onChange={(e) => setMinStrength(parseInt(e.target.value) / 100)}
            className="w-24"
          />
          <span className="text-sm w-10">{(minStrength * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Signals Table */}
      <DataTable
        columns={columns}
        data={data?.signals || []}
        keyExtractor={(s) => s.id}
        loading={isLoading}
        emptyMessage="No signals match your filters"
      />
    </div>
  );
}

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
}
